"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { AddToCartProduct } from "@/types/product";
import { FALLBACK_IMAGE } from "@/lib/shared/utils";
import { getStock } from "@/lib/commerce/stock";
import { useAnalytics } from "@/lib/analytics/client";
import { trackClientEvent, AnalyticsEvents } from "@/lib/analytics/events";

interface AddToCartButtonProps {
  product: AddToCartProduct;
  sizes: string[];
  stock?: Record<string, number>;
  archiveMode?: boolean;
}

export default function AddToCartButton({
  product,
  sizes,
  stock,
  archiveMode,
}: AddToCartButtonProps) {
  const { addItem } = useCart();
  const analytics = useAnalytics();
  const [selectedSize, setSelectedSize] = useState<string | undefined>();
  const [isAdding, setIsAdding] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isPreview = product.product_visibility === "preview";
  const hasAnyStock = sizes.some((size) => getStock(stock || product.stock, size) > 0);
  const isOutOfStock = !product.is_preorder && !hasAnyStock;

  const primaryImage =
    product?.images && product.images.length > 0
      ? product.images[0] || FALLBACK_IMAGE
      : FALLBACK_IMAGE;

  const handleAddToCart = async () => {
    if (isOutOfStock) {
      toast.error(`${product.name} is out of stock`);
      return;
    }
    if (!selectedSize && sizes.length > 0) {
      toast.error("Please select a size");
      return;
    }

    const sizeToAdd = selectedSize || sizes[0] || "";

    if (!product.is_preorder && getStock(stock || product.stock, sizeToAdd) <= 0) {
      toast.error(`Size ${sizeToAdd} is out of stock`);
      return;
    }

    setIsAdding(true);

    let priceNum = product.price;
    if (isNaN(priceNum)) priceNum = 0;

    addItem({
      id: product.id,
      name: product.name,
      price: priceNum,
      image: primaryImage,
      size: sizeToAdd,
      quantity: 1,
      stock: stock || product.stock,
      is_preorder: product.is_preorder,
      purchase_limit: product.purchase_limit ?? null,
      preorder_limit: product.preorder_limit ?? null,
    });
    trackClientEvent(analytics, AnalyticsEvents.AddToCart, {
      value: priceNum,
      currency: "eur",
      items: [
        {
          item_id: String(product.id),
          item_name: product.name,
          item_variant: sizeToAdd ?? undefined,
          price: priceNum,
          quantity: 1,
        },
      ],
    });
    setIsAdding(false);
    setSelectedSize(undefined);
    toast.success(`${product.name} (${sizeToAdd}) added to cart`);
  };

  if (isPreview) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-1">
          <div className="via-muted-foreground/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
          <p className="text-muted-foreground text-[10px] font-black tracking-[0.3em] uppercase">
            Preview only
          </p>
          <div className="via-muted-foreground/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
        </div>
        <Button
          disabled
          className="border-muted bg-muted text-muted-foreground relative h-12 w-full cursor-not-allowed border-2 text-sm font-black tracking-[0.25em] uppercase transition-all duration-300 sm:h-14 sm:text-base"
        >
          <span className="relative z-10">View only</span>
        </Button>
      </div>
    );
  }

  if (archiveMode) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-1">
          <div className="via-muted-foreground/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
          <p className="text-muted-foreground text-[10px] font-black tracking-[0.3em] uppercase">
            Archive
          </p>
          <div className="via-muted-foreground/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
        </div>
        <Button
          disabled
          className="border-muted bg-muted text-muted-foreground relative h-12 w-full cursor-not-allowed border-2 text-sm font-black tracking-[0.25em] uppercase transition-all duration-300 sm:h-14 sm:text-base"
        >
          <span className="relative z-10">No Longer Available</span>
        </Button>
      </div>
    );
  }

  if (isOutOfStock) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-1">
          <div className="via-destructive/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
          <p className="text-destructive text-[10px] font-black tracking-[0.3em] uppercase">
            Out of Stock
          </p>
          <div className="via-destructive/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
        </div>
        <div className="group relative">
          <div className="from-muted via-muted/80 to-muted absolute inset-0 bg-linear-to-r opacity-50" />
          <Button
            disabled
            className="border-muted bg-muted text-muted-foreground relative h-12 w-full cursor-not-allowed border-2 text-sm font-black tracking-[0.25em] uppercase transition-all duration-300 sm:h-14 sm:text-base"
          >
            <span className="relative z-10">Sold Out</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[500px] space-y-5">
      {sizes.length > 0 && (
        <div className="space-y-3">
          {/* Size Buttons */}
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 sm:gap-3 md:grid-cols-6 lg:grid-cols-4 xl:grid-cols-5">
            {sizes.map((size) => {
              const isSelected = selectedSize === size;
              const sizeStock = getStock(stock || product.stock, size);
              const isSizeOutOfStock = !product.is_preorder && sizeStock <= 0;

              return (
                <button
                  key={size}
                  onClick={() =>
                    !isSizeOutOfStock && setSelectedSize(isSelected ? undefined : size)
                  }
                  disabled={isAdding || isSizeOutOfStock}
                  className={`group relative h-11 overflow-hidden border-2 transition-all duration-300 ease-out sm:h-12 ${
                    isSelected
                      ? "border-foreground bg-foreground text-background scale-105 cursor-pointer shadow-lg"
                      : isSizeOutOfStock
                        ? "border-muted bg-muted text-muted-foreground/50 cursor-not-allowed"
                        : "border-foreground/20 bg-background text-foreground hover:border-foreground cursor-pointer hover:scale-105 hover:shadow-md"
                  } disabled:opacity-80 disabled:hover:scale-100`}
                >
                  {/* Background Animation (only valid items) */}
                  {!isSizeOutOfStock && (
                    <div
                      className={`bg-foreground absolute inset-0 transition-transform duration-300 ease-out ${
                        isSelected ? "translate-y-0" : "-translate-y-full group-hover:translate-y-0"
                      } `}
                    />
                  )}

                  {/* Size Text */}
                  <span
                    className={`relative z-10 block text-xs font-black tracking-[0.15em] uppercase transition-colors duration-300 sm:text-sm ${
                      isSelected
                        ? "text-background"
                        : isSizeOutOfStock
                          ? "text-muted-foreground/50"
                          : "text-foreground group-hover:text-background"
                    } ${isSizeOutOfStock ? "line-through decoration-1" : ""}`}
                  >
                    {size}
                  </span>

                  {/* Selected Indicator */}
                  {isSelected && (
                    <div className="absolute top-1 right-1 h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add to Cart Button */}
      <div className="relative pt-2">
        {/* Premium Separator */}
        <div className="via-foreground/20 absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent to-transparent" />

        <button
          onClick={handleAddToCart}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={isAdding || (sizes.length > 0 && !selectedSize)}
          className={`group border-foreground relative h-12 w-full overflow-hidden border-2 transition-all duration-500 ease-out sm:h-14 ${
            isAdding || (sizes.length > 0 && !selectedSize)
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer hover:scale-[1.02] hover:shadow-2xl"
          } `}
        >
          {/* Animated Background Layers */}
          <div
            className={`bg-foreground absolute inset-0 transition-transform duration-500 ease-out ${isHovered && !isAdding ? "scale-105" : "scale-100"} `}
          />

          {/* Gradient Overlay on Hover */}
          <div
            className={`from-foreground via-muted-foreground/10 to-foreground absolute inset-0 bg-linear-to-r transition-opacity duration-500 ${isHovered && !isAdding ? "opacity-100" : "opacity-0"} `}
          />

          {/* Shine Effect */}
          <div
            className={`via-background/10 absolute inset-0 bg-linear-to-r from-transparent to-transparent transition-transform duration-700 ease-out ${
              isHovered && !isAdding ? "translate-x-full" : "-translate-x-full"
            } `}
          />

          {/* Button Content */}
          <div className="relative z-10 flex h-full items-center justify-center gap-4">
            {isAdding && (
              <div className="border-background/30 border-t-background h-5 w-5 animate-spin rounded-full border-2" />
            )}
            <span
              className={`text-background text-xs font-black tracking-[0.25em] uppercase transition-all duration-300 sm:text-base sm:tracking-[0.3em] ${isHovered && !isAdding ? "translate-x-0.5" : "translate-x-0"} `}
            >
              {isAdding ? "Adding to Cart…" : product.is_preorder ? "Pre-order" : "Add to Cart"}
            </span>

            {/* Arrow Indicator */}
            {!isAdding && (
              <span
                className={`text-background text-lg font-bold transition-all duration-300 ${
                  isHovered ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0"
                } `}
              >
                →
              </span>
            )}
          </div>

          {/* Bottom Accent Line */}
          <div
            className={`bg-background absolute bottom-0 left-0 h-1 transition-all duration-500 ease-out ${isHovered && !isAdding ? "w-full" : "w-0"} `}
          />
        </button>
      </div>
    </div>
  );
}
